import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccessTimeOutlined as TimeIcon,
  CalendarTodayOutlined as CalendarIcon,
  ContentCopyOutlined as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  LaunchOutlined as OpenIcon,
  LinkOutlined as LinkIcon,
  PeopleAltOutlined as PeopleIcon,
  RefreshRounded as RefreshIcon,
} from "@mui/icons-material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { Job, JobDetail } from "../types/job";
import {
  fetchJobDetails,
  generateFlyer,
  updateJob,
} from "../Services/jobService";

interface JobDetailSectionProps {
  job: Job;
  showExtras?: boolean;
  onJobUpdate?: (updatedJob: JobDetail) => void;
}

type FeedbackSeverity = "success" | "error" | "warning" | "info";

const COLORS = {
  blue: "#1D4ED8",
  blueSoft: "#EFF6FF",
  blueBorder: "#BFDBFE",
  green: "#15803D",
  greenSoft: "#F0FDF4",
  greenBorder: "#BBF7D0",
  amber: "#9A3412",
  amberSoft: "#FFF7ED",
  amberBorder: "#FED7AA",
  slate: "#475569",
  border: "#E2E8F0",
  surface: "#F8FAFC",
};

const INVALID_DATE = "1990-01-01:00:00:00";

const formatDate = (value?: string) => {
  if (!value || value === INVALID_DATE) return "Not specified";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not specified";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDateForInput = (value?: string) => {
  if (!value || value === INVALID_DATE) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

const getTomorrowForInput = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split("T")[0];
};

const SectionCard: React.FC<{
  title: string;
  helper?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, helper, action, children }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={1.5}
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography variant="h6" fontWeight={750}>
          {title}
        </Typography>
        {helper && (
          <Typography variant="body2" color="text.secondary">
            {helper}
          </Typography>
        )}
      </Box>
      {action}
    </Stack>
    {children}
  </Paper>
);

const MetricCard: React.FC<{
  label: string;
  value: React.ReactNode;
  helper?: string;
  accent?: string;
}> = ({ label, value, helper, accent = COLORS.slate }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      height: "100%",
      borderRadius: 2,
      borderColor: COLORS.border,
      bgcolor: "background.paper",
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 700, letterSpacing: ".04em" }}
    >
      {label}
    </Typography>
    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800, color: accent }}>
      {value}
    </Typography>
    {helper && (
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
    )}
  </Paper>
);

const JobDetailSection: React.FC<JobDetailSectionProps> = ({
  job,
  onJobUpdate,
}) => {
  const [jobDetail, setJobDetail] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deadlineDialogOpen, setDeadlineDialogOpen] = useState(false);
  const [newDeadline, setNewDeadline] = useState("");
  const [updatingDeadline, setUpdatingDeadline] = useState(false);
  const [regeneratingFlyer, setRegeneratingFlyer] = useState(false);
  const [showFlyerRegenPrompt, setShowFlyerRegenPrompt] = useState(false);
  const [deadlineWasExtended, setDeadlineWasExtended] = useState(false);
  const [flyerFailed, setFlyerFailed] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as FeedbackSeverity,
  });

  const showFeedback = (
    message: string,
    severity: FeedbackSeverity = "success",
  ) => setSnackbar({ open: true, message, severity });

  const loadJobDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJobDetails(job.id);
      setJobDetail(data);
      setFlyerFailed(false);
    } catch (loadError) {
      console.error("Failed to fetch job details:", loadError);
      setError("We could not load the job details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  useEffect(() => {
    loadJobDetails();
  }, [loadJobDetails]);

  const applicationUrl = useMemo(
    () =>
      jobDetail
        ? `https://hiresmart.pineuron.com/availablejobs/${
            jobDetail.candidateFormId || job.id
          }`
        : "",
    [job.id, jobDetail],
  );

  const normalizedStatus = (jobDetail?.status || "").toLowerCase();
  const isOpen = normalizedStatus === "open";
  const isInReview = ["in review", "inreview", "processing"].includes(
    normalizedStatus,
  );

  const deadlinePassed = useMemo(() => {
    if (!jobDetail?.deadline || jobDetail.deadline === INVALID_DATE) {
      return false;
    }

    const deadline = new Date(jobDetail.deadline);
    if (Number.isNaN(deadline.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deadline.setHours(0, 0, 0, 0);
    return deadline < today;
  }, [jobDetail?.deadline]);

  const pendingApplications = useMemo(() => {
    if (!jobDetail?.stats) return 0;
    return Math.max(
      0,
      jobDetail.stats.total -
        jobDetail.stats.shortlisted -
        jobDetail.stats.finalized -
        jobDetail.stats.rejected,
    );
  }, [jobDetail?.stats]);

  const canExtendDeadline = Boolean(
    jobDetail &&
    isOpen &&
    jobDetail.deadline &&
    jobDetail.deadline !== INVALID_DATE,
  );

  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showFeedback(`${label} copied.`);
    } catch (copyError) {
      console.error("Clipboard copy failed:", copyError);
      showFeedback(`Could not copy ${label.toLowerCase()}.`, "error");
    }
  };

  const handleExtendDeadline = () => {
    if (!canExtendDeadline || !jobDetail) return;
    setNewDeadline(formatDateForInput(jobDetail.deadline));
    setDeadlineDialogOpen(true);
  };

  const handleRegenerateFlyer = async () => {
    if (!jobDetail) return;
    setRegeneratingFlyer(true);
    setShowFlyerRegenPrompt(false);
    try {
      const formLink = `https://hiresmart.pineuron.com/availablejobs/${jobDetail.jobId}`;
      const response = await generateFlyer(jobDetail.jobId, formLink);
      const flyerUrl =
        response.flyerImagePath || response.flyerUrl || jobDetail.flyerImageURl;

      if (!flyerUrl) throw new Error("Flyer URL was not returned.");

      const updatedJobDetail = {
        ...jobDetail,
        flyerImageURl: `${flyerUrl.split("?")[0]}?t=${Date.now()}`,
      };
      setJobDetail(updatedJobDetail);
      setFlyerFailed(false);
      onJobUpdate?.(updatedJobDetail);
      showFeedback("Flyer updated successfully.");
    } catch (flyerError) {
      console.error("Failed to regenerate flyer:", flyerError);
      showFeedback(
        "The flyer could not be updated. Please try again.",
        "error",
      );
    } finally {
      setRegeneratingFlyer(false);
      setDeadlineWasExtended(false);
    }
  };

  const handleDeadlineUpdate = async () => {
    if (!jobDetail || !newDeadline) return;

    const selected = new Date(newDeadline);
    const current = new Date(jobDetail.deadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selected.setHours(0, 0, 0, 0);
    current.setHours(0, 0, 0, 0);

    if (selected <= today) {
      showFeedback("Please select a future date.", "error");
      return;
    }
    if (selected <= current) {
      showFeedback(
        "The new deadline must be later than the current deadline.",
        "error",
      );
      return;
    }

    setUpdatingDeadline(true);
    try {
      const updateData = {
        id: jobDetail.jobId,
        title: jobDetail.jobTitle,
        description: jobDetail.jobDescription || "",
        AdditionalInformation: jobDetail.additionalInformation || "",
        location: jobDetail.location || "",
        deadline: newDeadline,
        linkedinPost: jobDetail.linkedinContent || "",
        screeningQuestions: jobDetail.screeningQuestions || [],
        FlyerJobDescriptions: [],
        FlyerJobSpecifications: [],
        emailContent: jobDetail.emailContent || "",
      };

      await updateJob(jobDetail.jobId, updateData);
      const updatedJobDetail = { ...jobDetail, deadline: newDeadline };
      setJobDetail(updatedJobDetail);
      onJobUpdate?.(updatedJobDetail);
      setDeadlineDialogOpen(false);
      setDeadlineWasExtended(true);
      setShowFlyerRegenPrompt(Boolean(jobDetail.flyerImageURl));
      showFeedback("Application deadline extended.");
    } catch (updateError) {
      console.error("Failed to update deadline:", updateError);
      showFeedback(
        "The deadline could not be extended. Please try again.",
        "error",
      );
    } finally {
      setUpdatingDeadline(false);
    }
  };

  const handleLinkedInShare = () => {
    if (!jobDetail) return;
    const clientId = "770p225r9lry32";
    const redirectUri = "https://hiresmart.pineuron.com/linkedin/callback";
    const scope = "openid%20profile%20w_member_social%20email";
    const state = encodeURIComponent(
      btoa(JSON.stringify({ jobId: jobDetail.jobId, timestamp: Date.now() })),
    );
    window.location.href =
      `https://www.linkedin.com/oauth/v2/authorization?response_type=code` +
      `&client_id=${clientId}&redirect_uri=${redirectUri}` +
      `&scope=${scope}&state=${state}&prompt=consent`;
  };

  if (loading) {
    return (
      <Stack
        spacing={2}
        sx={{ p: { xs: 1, md: 2 } }}
        aria-label="Loading job details"
      >
        <Skeleton variant="rounded" height={92} />
        <Grid container spacing={1.5}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={6} md={3} key={item}>
              <Skeleton variant="rounded" height={96} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={220} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={<Button onClick={loadJobDetails}>Try again</Button>}
        sx={{ alignItems: "center" }}
      >
        {error}
      </Alert>
    );
  }

  if (!jobDetail) return null;

  return (
    <>
      <Stack spacing={2.5}>
        {showFlyerRegenPrompt && (
          <Alert
            severity="info"
            onClose={() => setShowFlyerRegenPrompt(false)}
            action={
              <Button
                onClick={handleRegenerateFlyer}
                disabled={regeneratingFlyer}
              >
                {regeneratingFlyer ? "Updating…" : "Update flyer"}
              </Button>
            }
          >
            The deadline changed. Update the flyer so candidates see the correct
            date.
          </Alert>
        )}

        {isOpen && deadlinePassed && (
          <Alert
            severity="warning"
            action={
              <Button onClick={handleExtendDeadline}>Extend deadline</Button>
            }
          >
            This job is still open, but its application deadline has passed.
          </Alert>
        )}

        {canExtendDeadline && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={handleExtendDeadline}
              sx={{ minHeight: 42, textTransform: "none" }}
            >
              Extend deadline
            </Button>
          </Box>
        )}

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="REVIEW STARTED"
              value={formatDate(jobDetail.reviewStarted)}
              helper={
                jobDetail.reviewStarted ? "Processing timeline" : "Not started"
              }
              accent={isInReview ? COLORS.amber : COLORS.slate}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="APPLICATIONS"
              value={jobDetail.stats?.total ?? 0}
              helper="Total received"
              accent={COLORS.blue}
            />
          </Grid>
          {/* <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="SHORTLISTED"
              value={jobDetail.stats?.shortlisted ?? 0}
              helper="Selected for next step"
              accent={COLORS.green}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              label="FINALIZED"
              value={jobDetail.stats?.finalized ?? 0}
              helper="Hiring decisions completed"
              accent={COLORS.green}
            />
          </Grid> */}
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <Stack spacing={2}>
              <SectionCard
                title="Job description"
                helper="What candidates will see about this role"
              >
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "pre-wrap", lineHeight: 1.75 }}
                >
                  {jobDetail.jobDescription ||
                    "No job description has been added."}
                </Typography>
                {jobDetail.additionalInformation && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: COLORS.surface,
                      borderRadius: 1.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={750}
                      sx={{ mb: 0.5 }}
                    >
                      Additional information
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }}
                    >
                      {jobDetail.additionalInformation}
                    </Typography>
                  </Box>
                )}
              </SectionCard>

              {jobDetail.screeningQuestions?.length > 0 && (
                <SectionCard
                  title="Screening questions"
                  helper={`${jobDetail.screeningQuestions.length} question${jobDetail.screeningQuestions.length === 1 ? "" : "s"} shown to applicants`}
                >
                  <List disablePadding>
                    {jobDetail.screeningQuestions.map((question, index) => (
                      <ListItem
                        key={`${question}-${index}`}
                        alignItems="flex-start"
                        sx={{
                          px: 0,
                          py: 1,
                          borderBottom:
                            index < jobDetail.screeningQuestions.length - 1
                              ? 1
                              : 0,
                          borderColor: "divider",
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            mr: 1.25,
                            mt: 0.25,
                            flexShrink: 0,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: "50%",
                            bgcolor: COLORS.blueSoft,
                            color: COLORS.blue,
                            fontWeight: 800,
                            fontSize: 13,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <ListItemText
                          primary={question}
                          primaryTypographyProps={{
                            variant: "body2",
                            lineHeight: 1.6,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </SectionCard>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Stack spacing={2}>
              {jobDetail.flyerImageURl && (
                <SectionCard
                  title="Job flyer"
                  helper="Preview the current candidate-facing flyer"
                  action={
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        startIcon={<OpenIcon />}
                        href={jobDetail.flyerImageURl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: "none" }}
                      >
                        Open
                      </Button>
                      {deadlineWasExtended && (
                        <Button
                          size="small"
                          startIcon={
                            regeneratingFlyer ? (
                              <CircularProgress size={16} />
                            ) : (
                              <RefreshIcon />
                            )
                          }
                          onClick={handleRegenerateFlyer}
                          disabled={regeneratingFlyer}
                          sx={{ textTransform: "none" }}
                        >
                          Update
                        </Button>
                      )}
                    </Stack>
                  }
                >
                  {flyerFailed ? (
                    <Alert severity="warning">
                      The flyer preview could not be displayed. Use Open to view
                      it in a new tab.
                    </Alert>
                  ) : (
                    <Box
                      component="img"
                      src={jobDetail.flyerImageURl}
                      alt={`Job flyer for ${jobDetail.jobTitle}`}
                      onError={() => setFlyerFailed(true)}
                      sx={{
                        display: "block",
                        width: "100%",
                        maxHeight: 560,
                        objectFit: "contain",
                        borderRadius: 1.5,
                        border: 1,
                        borderColor: "divider",
                        bgcolor: COLORS.surface,
                      }}
                    />
                  )}
                </SectionCard>
              )}

              <SectionCard
                title="Application form"
                helper="Share this link with candidates"
              >
                <TextField
                  fullWidth
                  size="small"
                  value={applicationUrl}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Copy application link">
                          <IconButton
                            edge="end"
                            aria-label="Copy application link"
                            onClick={() =>
                              handleCopyToClipboard(
                                applicationUrl,
                                "Application link",
                              )
                            }
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<OpenIcon />}
                  href={applicationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mt: 1.5,
                    minHeight: 42,
                    bgcolor: COLORS.blue,
                    boxShadow: "none",
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "#1E40AF", boxShadow: "none" },
                  }}
                >
                  Open application form
                </Button>
              </SectionCard>
            </Stack>
          </Grid>
        </Grid>

        {/* {jobDetail.stats && (
          <SectionCard
            title="Application progress"
            helper="Current recruitment outcome summary"
          >
            <Grid container spacing={1.5}>
              {[
                {
                  label: "Pending",
                  value: pendingApplications,
                  color: COLORS.slate,
                },
                {
                  label: "Shortlisted",
                  value: jobDetail.stats.shortlisted,
                  color: COLORS.blue,
                },
                {
                  label: "Finalized",
                  value: jobDetail.stats.finalized,
                  color: COLORS.green,
                },
                {
                  label: "Rejected",
                  value: jobDetail.stats.rejected,
                  color: "#B42318",
                },
              ].map((metric) => (
                <Grid item xs={6} md={3} key={metric.label}>
                  <Box
                    sx={{
                      p: 2,
                      textAlign: "center",
                      bgcolor: COLORS.surface,
                      borderRadius: 1.5,
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={850}
                      sx={{ color: metric.color }}
                    >
                      {metric.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {metric.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        )} */}

        {(jobDetail.linkedinContent || jobDetail.emailContent) && (
          <SectionCard
            title="Sharing content"
            helper="Ready-to-use content for promoting this vacancy"
          >
            <Stack spacing={1.5}>
              {jobDetail.linkedinContent && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "8px !important",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LinkedInIcon sx={{ color: "#0A66C2" }} />
                      <Typography fontWeight={700}>LinkedIn post</Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      multiline
                      fullWidth
                      minRows={4}
                      value={jobDetail.linkedinContent}
                      InputProps={{ readOnly: true }}
                    />
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{ mt: 1.5 }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CopyIcon />}
                        onClick={() =>
                          handleCopyToClipboard(
                            jobDetail.linkedinContent,
                            "LinkedIn content",
                          )
                        }
                        sx={{ minHeight: 42, textTransform: "none" }}
                      >
                        Copy content
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<LinkedInIcon />}
                        onClick={handleLinkedInShare}
                        sx={{
                          minHeight: 42,
                          bgcolor: "#0A66C2",
                          boxShadow: "none",
                          textTransform: "none",
                          "&:hover": { bgcolor: "#084F96", boxShadow: "none" },
                        }}
                      >
                        Share on LinkedIn
                      </Button>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {jobDetail.emailContent && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "8px !important",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={700}>Email content</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      multiline
                      fullWidth
                      minRows={4}
                      value={jobDetail.emailContent}
                      InputProps={{ readOnly: true }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CopyIcon />}
                      onClick={() =>
                        handleCopyToClipboard(
                          jobDetail.emailContent,
                          "Email content",
                        )
                      }
                      sx={{ mt: 1.5, minHeight: 42, textTransform: "none" }}
                    >
                      Copy content
                    </Button>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          </SectionCard>
        )}

        <Accordion
          disableGutters
          elevation={0}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "8px !important",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography fontWeight={700}>Technical identifiers</Typography>
              <Typography variant="body2" color="text.secondary">
                Job and form IDs for support or integration work
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {[
                { label: "Job ID", value: jobDetail.jobId },
                {
                  label: "Form ID",
                  value: jobDetail.candidateFormId || "Not available",
                },
              ].map((field) => (
                <Grid item xs={12} md={6} key={field.label}>
                  <Typography variant="caption" color="text.secondary">
                    {field.label}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={field.value}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={`Copy ${field.label}`}>
                            <IconButton
                              edge="end"
                              aria-label={`Copy ${field.label}`}
                              onClick={() =>
                                handleCopyToClipboard(field.value, field.label)
                              }
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mt: 0.5,
                      "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: ".82rem",
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Stack>

      <Dialog
        open={deadlineDialogOpen}
        onClose={() => !updatingDeadline && setDeadlineDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Extend application deadline</DialogTitle>
        <DialogContent>
          <Alert severity="info" icon={<TimeIcon />} sx={{ mb: 2 }}>
            Current deadline: <strong>{formatDate(jobDetail.deadline)}</strong>
          </Alert>
          <TextField
            fullWidth
            type="date"
            label="New deadline"
            value={newDeadline}
            onChange={(event) => setNewDeadline(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: getTomorrowForInput() }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeadlineDialogOpen(false)}
            disabled={updatingDeadline}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeadlineUpdate}
            variant="contained"
            disabled={!newDeadline || updatingDeadline}
            sx={{
              bgcolor: COLORS.blue,
              boxShadow: "none",
              textTransform: "none",
              "&:hover": { bgcolor: "#1E40AF", boxShadow: "none" },
            }}
          >
            {updatingDeadline ? "Extending…" : "Extend deadline"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar((current) => ({ ...current, open: false }))
          }
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default JobDetailSection;